import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '../src/lib/supabaseClient';
import { getRechargePlanById, YEARLY_MEMBERSHIP_PLAN } from './rechargePlans';
import type { RechargePlan, RechargePlanId } from './rechargePlans';
import RechargePlanCards from './RechargePlanCards';
import PlanCard from './PlanCard';
import BillingPeriodToggle from './BillingPeriodToggle';
import type { Lang } from '../utils/i18n';

/** 禁止把 Supabase Edge Function 当作支付完成后的浏览器跳转地址 */
function isSupabaseEdgeFunctionUrl(url: string): boolean {
  try {
    const u = new URL(url);
    return u.hostname.includes('supabase.co') && u.pathname.includes('/functions/v1/');
  } catch {
    return false;
  }
}

/**
 * 支付宝同步 return_url：必须是前端页面（积分到账依赖 notify_url 异步回调）。
 * 默认回到当前页并带 payment_success=true，与 App.tsx 支付成功检测一致。
 */
function buildFrontendPaymentReturnUrl(rawOverride?: string | null): string {
  const origin = window.location.origin;

  const resolveOverride = (raw: string): string => {
    if (raw.startsWith('http')) return raw;
    if (raw.startsWith('/')) return origin + raw;
    return `${origin}/${raw}`;
  };

  let base: string;
  if (rawOverride && !isSupabaseEdgeFunctionUrl(resolveOverride(rawOverride))) {
    base = resolveOverride(rawOverride);
  } else {
    const path = window.location.pathname || '/index-app.html';
    base = `${origin}${path}`;
  }

  try {
    const u = new URL(base);
    u.searchParams.set('payment_success', 'true');
    u.searchParams.delete('open_payment_center');
    u.searchParams.delete('recharge_plan');
    u.searchParams.delete('return_url_override');
    return `${u.origin}${u.pathname}${u.search}`;
  } catch {
    return `${origin}/index-app.html?payment_success=true`;
  }
}

interface PaymentCenterProps {
  userEmail: string;
  language?: Lang;
  onClose: () => void;
  /** Theme sync with official landing page (light/dark). Default: dark. */
  isDarkMode?: boolean;
  /** If set, auto-start payment for that plan when email is confirmed */
  autoRechargePlanId?: RechargePlanId;
  /** Override the payment return target (used by official landing page). */
  returnUrlOverride?: string | null;
}

const PaymentCenter: React.FC<PaymentCenterProps> = ({
  userEmail,
  language = 'zh',
  isDarkMode = true,
  onClose,
  autoRechargePlanId,
  returnUrlOverride,
}) => {
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [loadingAmount, setLoadingAmount] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isEmailConfirmed, setIsEmailConfirmed] = useState<boolean | null>(null);
  const autoStartedRef = useRef(false);

  const isEn = language === 'en';

  useEffect(() => {
    const checkEmailConfirmation = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          const confirmed = (session.user.email_confirmed_at != null) ||
            ((session.user as any).confirmed_at != null) ||
            ((session.user as any).email_verified === true);
          setIsEmailConfirmed(confirmed);

          if (!confirmed) {
            setError(isEn
              ? 'Please verify your email before recharging. Check your email for the confirmation link.'
              : '请先验证您的邮箱后再充值。请检查您的邮箱并点击确认链接。');
          }
        } else {
          setIsEmailConfirmed(false);
        }
      } catch (err) {
        console.error('检查邮箱确认状态失败:', err);
        setIsEmailConfirmed(false);
      }
    };

    checkEmailConfirmation();
  }, [isEn]);

  useEffect(() => {
    if (autoRechargePlanId === 'yearly') setBillingPeriod('yearly');
  }, [autoRechargePlanId]);

  const handleRecharge = async (plan: RechargePlan) => {
    if (isEmailConfirmed === false) {
      setError(isEn
        ? 'Please verify your email before recharging. Check your email for the confirmation link.'
        : '请先验证您的邮箱后再充值。请检查您的邮箱并点击确认链接。');
      return;
    }

    setLoadingAmount(plan.amount);
    setError(null);

    // 同步 return_url：仅前端页面；积分到账由支付宝 notify_url 异步处理。
    const searchParams = new URLSearchParams(window.location.search);
    const rawOverride = returnUrlOverride ?? searchParams.get('return_url_override');
    const resolvedReturnUrl = buildFrontendPaymentReturnUrl(rawOverride);
    const returnUrl = encodeURIComponent(resolvedReturnUrl);

    console.log('发起充值:', {
      amount: plan.amount,
      credits: plan.credits,
      returnUrl: resolvedReturnUrl,
    });

    const payBase = `https://soxtznsucksmsfmyjoym.supabase.co/functions/v1/alipay-pay`;
    const payUrl = `${payBase}?amount=${plan.amount.toFixed(2)}&user_email=${encodeURIComponent(
      userEmail
    )}&return_url=${returnUrl}&json=1`;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;

      if (!session || !accessToken) {
        setError(isEn ? 'Login session expired. Please sign in again.' : '登录状态已失效，请重新登录');
        setLoadingAmount(null);
        return;
      }

      const res = await fetch(payUrl, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
      });
      const data = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        redirect?: string;
        code?: string;
        message?: string;
        error?: string;
      };
      if (res.status === 403 && data.code === 'RECHARGE_BANNED') {
        setError(
          isEn
            ? 'This account cannot recharge. Please contact support.'
            : '该账号暂不可充值，如有疑问请联系客服。'
        );
        setLoadingAmount(null);
        return;
      }
      if (!res.ok) {
        setError(
          isEn
            ? `Payment start failed: ${data.message || data.error || res.statusText}`
            : `无法发起支付：${data.message || data.error || res.statusText}`
        );
        setLoadingAmount(null);
        return;
      }
      if (data.ok && data.redirect) {
        const shouldReplaceHistory = !!autoRechargePlanId || !!rawOverride;
        if (shouldReplaceHistory) {
          window.location.replace(data.redirect);
        } else {
          window.location.assign(data.redirect);
        }
        return;
      }
      setError(isEn ? 'Invalid payment response.' : '支付接口返回异常，请稍后重试。');
    } catch (e: any) {
      setError(isEn ? `Network error: ${e?.message}` : `网络错误：${e?.message || '请稍后重试'}`);
    } finally {
      setLoadingAmount(null);
    }
  };

  // Auto-trigger payment when coming from the official landing page.
  useEffect(() => {
    if (!autoRechargePlanId) return;
    if (autoStartedRef.current) return;
    if (isEmailConfirmed !== true) return;

    const plan = getRechargePlanById(autoRechargePlanId);
    if (!plan) {
      setError(isEn ? 'Invalid recharge plan.' : '无效的充值套餐。');
      autoStartedRef.current = true;
      return;
    }

    autoStartedRef.current = true;
    handleRecharge(plan);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRechargePlanId, isEmailConfirmed, isEn]);

  const label = (zh: string, en: string) => (isEn ? en : zh);
  const overlayClass = isDarkMode ? 'bg-black/85' : 'bg-black/55';
  const modalBg = isDarkMode ? '#000000' : '#ffffff';
  const modalBorder = isDarkMode ? '1px solid rgba(212, 163, 115, 0.35)' : '1px solid rgba(0, 0, 0, 0.08)';
  const modalShadow = isDarkMode
    ? '0 0 48px rgba(212, 163, 115, 0.15), inset 0 0 0 1px rgba(255,255,255,0.04)'
    : '0 0 48px rgba(212, 163, 115, 0.10), inset 0 0 0 1px rgba(0,0,0,0.02)';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={`fixed inset-0 z-[3000] flex items-center justify-center ${overlayClass} backdrop-blur-md p-3 sm:p-4`}
      onClick={onClose}
      style={{ zIndex: 3000 }}
    >
      <motion.div
        initial={{ scale: 0.98, opacity: 0, y: 12 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.98, opacity: 0, y: 12 }}
        transition={{ duration: 0.2 }}
        className="relative w-full max-w-[min(1760px,calc(100vw-1.5rem))] max-h-[92vh] overflow-y-auto rounded-2xl"
        onClick={(e) => e.stopPropagation()}
        style={{
          zIndex: 3001,
          background: modalBg,
          border: modalBorder,
          boxShadow: modalShadow,
        }}
      >
        <button
          type="button"
          onClick={onClose}
          className={`absolute top-3 right-3 sm:top-4 sm:right-4 z-10 w-9 h-9 rounded-full flex items-center justify-center ${
            isDarkMode ? 'text-white/45 hover:text-white/90 hover:bg-white/5' : 'text-black/45 hover:text-black/80 hover:bg-black/5'
          } transition-colors`}
          aria-label="Close"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="px-4 pt-10 pb-8 sm:px-8 sm:pt-12 sm:pb-10 md:px-10">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-6 mb-2">
            <h2 className={`text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
              {label('订阅计划', 'Subscription plans')}
            </h2>
            <BillingPeriodToggle
              value={billingPeriod}
              onChange={setBillingPeriod}
              language={language === 'zh' ? 'zh' : 'en'}
              isDarkMode={isDarkMode}
            />
          </div>
          <p className={`text-sm mb-8 sm:mb-10 max-w-2xl ${isDarkMode ? 'text-white/45' : 'text-zinc-600/75'}`}>
            {label(
              '选择套餐并点击按钮跳转支付；支付成功后将返回当前页并更新账户。',
              'Choose a plan and proceed to payment; after success you will return here with your balance updated.',
            )}
          </p>

          {error && (
            <div
              className={`mb-6 rounded-lg border px-4 py-3 text-sm ${
                isDarkMode
                  ? 'border-[#D7CDA1]/25 bg-[#D7CDA1]/10 text-[#e8c9a8]'
                  : 'border-[#D7CDA1]/35 bg-[#D7CDA1]/10 text-[#8b5e2e]'
              }`}
            >
              {error}
            </div>
          )}

          {billingPeriod === 'monthly' ? (
            <RechargePlanCards
              language={language === 'zh' ? 'zh' : 'en'}
              isDarkMode={isDarkMode}
              onPrimaryAction={handleRecharge}
              loadingAmount={loadingAmount}
              disabled={isEmailConfirmed === false}
            />
          ) : (
            <div className="flex justify-center px-0">
              <div className="w-full max-w-[460px]">
                <PlanCard
                  plan={YEARLY_MEMBERSHIP_PLAN}
                  language={language === 'zh' ? 'zh' : 'en'}
                  isDarkMode={isDarkMode}
                  onPrimaryAction={handleRecharge}
                  loadingAmount={loadingAmount}
                  disabled={isEmailConfirmed === false}
                />
              </div>
            </div>
          )}

          <p className={`mt-8 text-center text-[11px] leading-relaxed max-w-3xl mx-auto ${isDarkMode ? 'text-white/35' : 'text-zinc-500/70'}`}>
            {label(
              '点击套餐内按钮即可跳转到支付页面。支付成功后会自动返回。网页缓冲，敬请谅解。',
              'Click a plan button to open the payment page. You will return after success. Thank you for your patience.',
            )}
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default PaymentCenter;
